import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Row,
  Col,
  message,
  Tag,
  Space,
  Typography,
  Divider,
  Alert,
  Tabs,
} from 'antd';
import {
  PhoneOutlined,
  SettingOutlined,
  SaveOutlined,
  ApiOutlined,
  RobotOutlined,
  LinkOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { callCenterSettingsApi, show422Error, ValidationError } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import PermissionGate from '../../components/PermissionGate';

const { Text } = Typography;

const CopyableField = ({ value, label }) => {
  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      message.success(`${label} کپی شد`);
    } catch {
      message.error('خطا در کپی کردن');
    }
  };

  return (
    <Input
      value={value || ''}
      readOnly
      className="custom-font"
      dir="ltr"
      suffix={
        <Button
          type="text"
          size="small"
          icon={<CopyOutlined />}
          onClick={handleCopy}
          disabled={!value}
          title="کپی"
        />
      }
    />
  );
};

const CallCenterSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [settingsMeta, setSettingsMeta] = useState(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callCenterSettingsApi.getSettings();
      const data = res?.data ?? res;
      setSettingsMeta(data);

      form.setFieldsValue({
        driver: data.driver || 'asterisk',
        ami_host: data.ami_host || '',
        ami_port: data.ami_port ?? 5038,
        ami_user: data.ami_user || '',
        ami_pass: '',
        ami_context: data.ami_context || 'from-internal',
        ami_channel_tech: data.ami_channel_tech || 'PJSIP',
        cdr_api_url: data.cdr_api_url || '',
        ai_base_url: data.ai_base_url || '',
        ai_token: '',
        ai_enabled: data.ai_enabled ?? false,
        is_active: data.is_active ?? true,
        transcribe_api_url: data.transcribe_api_url || '',
        transcribe_api_key: '',
      });
    } catch (error) {
      console.error('خطا در دریافت تنظیمات مرکز تماس:', error);
      message.error('خطا در دریافت تنظیمات مرکز تماس');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        driver: values.driver || 'asterisk',
        ami_host: values.ami_host?.trim(),
        ami_port: Number(values.ami_port) || 5038,
        ami_user: values.ami_user?.trim(),
        ami_context: values.ami_context?.trim() || 'from-internal',
        ami_channel_tech: values.ami_channel_tech || 'PJSIP',
        cdr_api_url: values.cdr_api_url?.trim() || null,
        ai_base_url: values.ai_base_url?.trim() || null,
        ai_enabled: Boolean(values.ai_enabled),
        is_active: Boolean(values.is_active),
        transcribe_api_url: values.transcribe_api_url?.trim() || null,
      };

      if (values.ami_pass?.trim()) {
        payload.ami_pass = values.ami_pass.trim();
      }
      if (values.ai_token?.trim()) {
        payload.ai_token = values.ai_token.trim();
      }
      if (values.transcribe_api_key?.trim()) {
        payload.transcribe_api_key = values.transcribe_api_key.trim();
      }

      await callCenterSettingsApi.updateSettings(payload);
      message.success('تنظیمات مرکز تماس با موفقیت ذخیره شد');
      form.setFieldsValue({ ami_pass: '', ai_token: '', transcribe_api_key: '' });
      await loadSettings();
    } catch (error) {
      console.error('خطا در ذخیره تنظیمات:', error);
      if (error instanceof ValidationError) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ذخیره تنظیمات');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await callCenterSettingsApi.testConnection();
      const data = res?.data ?? {};
      setTestResult(data);
      const count = data.endpoints_count ?? (data.endpoints?.length ?? 0);
      message.success(
        res?.message || `ارتباط با Asterisk AMI با موفقیت برقرار شد (${count} داخلی شناسایی شد)`,
      );
    } catch (error) {
      console.error('خطا در تست اتصال:', error);
      if (error instanceof ValidationError) {
        show422Error(error);
      } else {
        message.error(error?.message || 'ارتباط با سرور Asterisk AMI برقرار نشد');
      }
    } finally {
      setTesting(false);
    }
  };

  const tabItems = [
    {
      key: 'asterisk',
      label: (
        <span className="custom-font flex items-center gap-2">
          <ApiOutlined />
          اتصال استریسک (Asterisk AMI)
        </span>
      ),
      children: (
        <div className="py-2">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4 p-3 bg-gray-50 dark:bg-gray-800/80 rounded-lg border border-gray-200 dark:border-gray-700">
            <div>
              <span className="custom-font font-medium text-gray-700 dark:text-gray-200">
                وضعیت اتصال و پیکربندی پروتکل مدیریت استریسک (AMI)
              </span>
              <p className="custom-font text-xs text-gray-500 dark:text-gray-400 m-0 mt-1">
                اتصال جهت ایجاد تماس با یک کلیک (Originate)، تشخیص داخلی‌های آزاد، و کنترل صف‌ها استفاده می‌شود.
              </p>
            </div>
            <Space>
              {settingsMeta && (
                <Tag
                  icon={settingsMeta.ami_pass_set ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  color={settingsMeta.ami_pass_set ? 'success' : 'warning'}
                  className="custom-font"
                >
                  {settingsMeta.ami_pass_set ? 'رمز عبور AMI تنظیم شده' : 'رمز عبور AMI تنظیم نشده'}
                </Tag>
              )}
              <Button
                type="default"
                icon={<ThunderboltOutlined />}
                loading={testing}
                onClick={handleTestConnection}
                className="custom-font text-teal-600 border-teal-500 hover:text-teal-700 hover:border-teal-600"
              >
                تست اتصال استریسک
              </Button>
            </Space>
          </div>

          {testResult && (
            <Card
              size="small"
              className="mb-6 border-emerald-400 dark:border-emerald-700 bg-emerald-50/60 dark:bg-emerald-950/20"
              title={
                <Space>
                  <CheckCircleOutlined className="text-emerald-500" />
                  <span className="custom-font font-medium text-emerald-800 dark:text-emerald-300">
                    نتیجه تست ارتباط با استریسک AMI
                  </span>
                </Space>
              }
              extra={
                <Button size="small" type="text" onClick={() => setTestResult(null)} className="custom-font">
                  بستن
                </Button>
              }
            >
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 text-xs block">هاست و پورت:</span>
                    <Tag className="custom-font font-mono mt-0.5" dir="ltr">{testResult.host}:{testResult.port}</Tag>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 text-xs block">نام کاربری:</span>
                    <Tag className="custom-font font-mono mt-0.5">{testResult.username}</Tag>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 text-xs block">وضعیت پینگ:</span>
                    <Tag color="success" className="custom-font font-mono mt-0.5">{testResult.ping || 'Pong'}</Tag>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 text-xs block">تعداد داخلی‌های شناسایی‌شده:</span>
                    <Tag color="blue" className="custom-font mt-0.5">
                      {testResult.endpoints_count ?? (testResult.endpoints?.length || 0)} داخلی
                    </Tag>
                  </div>
                </div>

                {testResult.endpoints && testResult.endpoints.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-emerald-200 dark:border-emerald-800">
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      داخلی‌های فعال روی سرور استریسک:
                    </div>
                    <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
                      {testResult.endpoints.map((ep) => (
                        <Tag key={ep} icon={<PhoneOutlined />} color="cyan" className="custom-font font-mono" dir="ltr">
                          {ep}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          <Form.Item name="driver" hidden initialValue="asterisk">
            <Input />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={16}>
              <Form.Item
                label="آدرس یا IP سرور استریسک (AMI Host)"
                name="ami_host"
                rules={[{ required: true, message: 'آدرس سرور استریسک الزامی است' }]}
              >
                <Input
                  placeholder="مثال: 127.0.0.1 یا 192.168.1.5"
                  className="custom-font"
                  dir="ltr"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="پورت AMI (پیش‌فرض: 5038)"
                name="ami_port"
                rules={[{ required: true, message: 'پورت الزامی است' }]}
              >
                <InputNumber
                  placeholder="5038"
                  className="custom-font w-full"
                  dir="ltr"
                  min={1}
                  max={65535}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="نام کاربری AMI (Username)"
                name="ami_user"
                rules={[{ required: true, message: 'نام کاربری AMI الزامی است' }]}
              >
                <Input
                  placeholder="مثال: admin"
                  className="custom-font"
                  dir="ltr"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="رمز عبور AMI (Secret)"
                name="ami_pass"
                extra={
                  settingsMeta?.ami_pass_set
                    ? 'رمز عبور فعلی ذخیره شده است. در صورت تمایل به تغییر، رمز جدید را وارد نمایید.'
                    : 'رمز عبور تعریف شده در manager.conf استریسک را وارد کنید.'
                }
              >
                <Input.Password
                  placeholder={settingsMeta?.ami_pass_set ? '••••••••••••' : 'رمز عبور AMI'}
                  className="custom-font"
                  dir="ltr"
                  autoComplete="new-password"
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="کانتکست تماس (AMI Context)"
                name="ami_context"
                rules={[{ required: true, message: 'کانتکست الزامی است' }]}
                extra="کانتکست دایل‌پلن برای برقراری تماس خروجی (مثال: from-internal)"
              >
                <Input
                  placeholder="from-internal"
                  className="custom-font"
                  dir="ltr"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="تکنولوژی کانال (Channel Tech)"
                name="ami_channel_tech"
                rules={[{ required: true, message: 'انتخاب تکنولوژی کانال الزامی است' }]}
                extra="پروتکل مورد استفاده در استریسک برای داخلی‌ها"
              >
                <Select
                  className="custom-font"
                  popupClassName="custom-font"
                  options={[
                    { value: 'PJSIP', label: 'PJSIP (پیش‌فرض و مدرن)' },
                    { value: 'SIP', label: 'SIP (chan_sip سنتی)' },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                label="آدرس وب‌سرویس لاگ تماس‌ها (CDR API URL)"
                name="cdr_api_url"
                extra="اختیاری — در صورت استفاده از وب‌سرویس اختصاصی برای بازیابی سوابق تماس و دانلود صوت (مثال: http://192.168.1.5/api/index.php)"
              >
                <Input
                  placeholder="http://192.168.1.5/api/index.php"
                  className="custom-font"
                  dir="ltr"
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'webhook',
      label: (
        <span className="custom-font flex items-center gap-2">
          <LinkOutlined />
          وب‌هوک و لاگ تماس‌ها
        </span>
      ),
      children: (
        <div className="py-2">
          <Alert
            type="info"
            showIcon
            className="mb-6 custom-font"
            message="سرویس شنود تماس‌ها (PBX Webhook Listener)"
            description={
              <div className="text-xs space-y-1.5 mt-1">
                <div>
                  رویدادهای لحظه‌ای تماس (مانند شروع زنگ و قطع مکالمه) و فایل ضبط مکالمات از طریق این وب‌هوک توسط اسکریپت
                  شنودگر استریسک (<code className="font-mono bg-blue-50 dark:bg-blue-900/40 px-1 py-0.5 rounded">listener.php</code>)
                  به سیستم ارسال و ثبت می‌شوند.
                </div>
              </div>
            }
          />

          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <div className="mb-4">
                <Text className="custom-font text-gray-600 dark:text-gray-400 block mb-1">
                  آدرس وب‌هوک (Webhook URL)
                </Text>
                <CopyableField value={settingsMeta?.webhook_url} label="آدرس وب‌هوک" />
                <span className="text-xs text-gray-400 block mt-1">
                  این آدرس را در اسکریپت شنودگر استریسک به عنوان مقصد ارسال رویدادها قرار دهید.
                </span>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div className="mb-4">
                <Text className="custom-font text-gray-600 dark:text-gray-400 block mb-1">
                  توکن امنیتی وب‌هوک (Webhook Token)
                </Text>
                <CopyableField value={settingsMeta?.webhook_token} label="توکن امنیتی وب‌هوک" />
              </div>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'ai',
      label: (
        <span className="custom-font flex items-center gap-2">
          <RobotOutlined />
          هوش مصنوعی و تحلیل صوتی
        </span>
      ),
      children: (
        <div className="py-2">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <span className="custom-font text-gray-600 dark:text-gray-400 text-sm">
              پیکربندی اتصال به سرویس هوش مصنوعی جهت تبدیل گفتار به متن (STT)، خلاصه‌سازی و تحلیل مکالمات
            </span>
            {settingsMeta && (
              <Tag
                icon={settingsMeta.ai_token_set ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                color={settingsMeta.ai_token_set ? 'success' : 'warning'}
                className="custom-font"
              >
                {settingsMeta.ai_token_set ? 'توکن AI تنظیم شده' : 'توکن AI تنظیم نشده'}
              </Tag>
            )}
          </div>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={14}>
              <Form.Item label="آدرس سرور هوش مصنوعی (AI Base URL)" name="ai_base_url">
                <Input
                  placeholder="مثال: http://37.255.236.220:8000"
                  className="custom-font"
                  dir="ltr"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item
                label="توکن امنیتی AI (Bearer Token)"
                name="ai_token"
                extra={
                  settingsMeta?.ai_token_set
                    ? 'توکن فعلی ذخیره شده است. برای تغییر، توکن جدید وارد کنید.'
                    : 'توکن دسترسی به سرویس هوش مصنوعی را وارد کنید.'
                }
              >
                <Input.Password
                  placeholder={settingsMeta?.ai_token_set ? '••••••••••••' : 'توکن هوش مصنوعی'}
                  className="custom-font"
                  dir="ltr"
                  autoComplete="new-password"
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label="فعال‌سازی هوش مصنوعی و خلاصه‌سازی خودکار مکالمات"
                name="ai_enabled"
                valuePropName="checked"
              >
                <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
              </Form.Item>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'transcribe',
      label: (
        <span className="custom-font flex items-center gap-2">
          <ApiOutlined />
          تنظیمات رونویسی (Transcription)
        </span>
      ),
      children: (
        <div className="py-2">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <span className="custom-font text-gray-600 dark:text-gray-400 text-sm">
              پیکربندی سرویس رونویسی (Transcription) مکالمات
            </span>
            {settingsMeta && (
              <Tag
                icon={settingsMeta.transcribe_api_key_set ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                color={settingsMeta.transcribe_api_key_set ? 'success' : 'warning'}
                className="custom-font"
              >
                {settingsMeta.transcribe_api_key_set ? 'کلید API تنظیم شده' : 'کلید API تنظیم نشده'}
              </Tag>
            )}
          </div>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={14}>
              <Form.Item label="آدرس سرور رونویسی (Transcription API URL)" name="transcribe_api_url">
                <Input
                  placeholder="مثال: http://example.com/transcribe"
                  className="custom-font"
                  dir="ltr"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item
                label="کلید API رونویسی"
                name="transcribe_api_key"
                extra={
                  settingsMeta?.transcribe_api_key_set
                    ? 'کلید فعلی ذخیره شده است. برای تغییر، کلید جدید وارد کنید.'
                    : 'کلید API سرویس رونویسی را وارد کنید.'
                }
              >
                <Input.Password
                  placeholder={settingsMeta?.transcribe_api_key_set ? '••••••••••••' : 'کلید API'}
                  className="custom-font"
                  dir="ltr"
                  autoComplete="new-password"
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: 'general',
      label: (
        <span className="custom-font flex items-center gap-2">
          <SettingOutlined />
          وضعیت عمومی ماژول
        </span>
      ),
      children: (
        <div className="py-2">
          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <Form.Item
                label="فعال بودن مرکز تماس در کل سامانه"
                name="is_active"
                valuePropName="checked"
                extra="در صورت غیرفعال بودن، دکمه‌های تماس و دریافت رویدادها غیرفعال می‌شوند."
              >
                <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
              </Form.Item>
            </Col>
          </Row>
        </div>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white flex items-center justify-center shadow-lg">
                <PhoneOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                  تنظیمات مرکز تماس (Asterisk AMI)
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  پیکربندی پروتکل Asterisk AMI، وب‌هوک دریافت رویدادهای تماس و هوش مصنوعی
                </p>
              </div>
            </div>
            <Space wrap>
              <Button
                icon={<ThunderboltOutlined />}
                loading={testing}
                onClick={handleTestConnection}
                className="custom-font"
              >
                تست اتصال استریسک
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-white dark:bg-gray-900 rounded-b-xl">
            {loading ? (
              <TableSkeleton rows={6} columns={2} />
            ) : (
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
                className="custom-font"
              >
                <Tabs
                  defaultActiveKey="asterisk"
                  items={tabItems}
                  className="custom-font"
                />

                <Divider />

                <div className="flex items-center justify-between flex-wrap gap-4">
                  <PermissionGate permission="manage_callcenter_setting">
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SaveOutlined />}
                      loading={saving}
                      className={PANEL_PRIMARY_BTN_CLASS}
                      size="large"
                    >
                      ذخیره تنظیمات مرکز تماس
                    </Button>
                  </PermissionGate>
                </div>
              </Form>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default CallCenterSettings;
